import type { AdminUserLogs, MealLogView, WorkoutSessionView } from '@steado/admin-contract/user-detail';
import { useQuery } from '@tanstack/react-query';

import { TONE_DOTS, type ToneName } from '@/components/table/tone';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';

import { Empty, Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from './TabError';
import { WeightChart } from './WeightChart';
import { formatDate, humanise } from '@/lib/format';
import { userLogsQueryOptions } from './user-detail-query';

const SESSION_TONES: Readonly<Record<string, ToneName>> = {
  completed: 'ok',
  in_progress: 'info',
  discarded: 'mute',
};

const SOURCE_TONES: Readonly<Record<string, ToneName>> = {
  manual: 'mute',
  photo_scan: 'purple',
  plan: 'info',
};

interface LogsTabProps {
  readonly userId: string;
}

export function LogsTab({ userId }: LogsTabProps) {
  const { api } = useAuth();
  const query = useQuery(userLogsQueryOptions(api, userId, true));

  if (query.isPending) return <TabSkeleton rows={4} />;
  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  return <LogsContent logs={query.data} />;
}

/** One chip in a day's row: what was logged, and where it came from. */
interface Entry {
  readonly key: string;
  readonly label: string;
  readonly source: string;
  readonly tone: ToneName;
}

interface Day {
  readonly localDate: string;
  readonly kcal: number;
  readonly entries: readonly Entry[];
}

/**
 * Meals and workouts on one timeline, grouped by the user's own local day.
 *
 * Two lists side by side made the obvious question — "what did this person
 * actually do on Tuesday" — into a scan of two panels with different orderings.
 * Both rows already carry `localDate`, resolved in the user's timezone, so the
 * grouping is the server's answer rather than a date this console computed from
 * a timestamp in the wrong zone.
 *
 * A day with nothing logged has no row, and the description says so: an empty
 * row per silent day would fill the card with nothing.
 */
function groupByDay(logs: AdminUserLogs): readonly Day[] {
  const days = new Map<string, { kcal: number; entries: Entry[] }>();

  const dayFor = (localDate: string) => {
    const existing = days.get(localDate);
    if (existing !== undefined) return existing;

    const created = { kcal: 0, entries: [] as Entry[] };
    days.set(localDate, created);
    return created;
  };

  for (const meal of logs.meals) {
    const day = dayFor(meal.localDate);
    day.kcal += meal.kcal;
    day.entries.push(mealEntry(meal));
  }

  for (const session of logs.workouts) {
    dayFor(session.localDate).entries.push(sessionEntry(session));
  }

  // Newest first, as the card's description promises.
  return [...days.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([localDate, day]) => ({ localDate, kcal: day.kcal, entries: day.entries }));
}

function mealEntry(meal: MealLogView): Entry {
  return {
    key: `meal:${meal.id}`,
    label: `${humanise(meal.mealType)} · ${String(meal.kcal)} kcal`,
    // What is in it is the sub-line: an operator scanning a month wants the
    // shape of the day, and reads the items only on the one day that is odd.
    source: meal.items.length === 0 ? humanise(meal.source) : meal.items.join(', '),
    tone: SOURCE_TONES[meal.source] ?? 'mute',
  };
}

function sessionEntry(session: WorkoutSessionView): Entry {
  return {
    key: `workout:${session.id}`,
    label: session.title ?? 'Untitled session',
    source: `${session.setCount === 1 ? '1 set' : `${String(session.setCount)} sets`}${
      session.perceivedExertion === null ? '' : ` · RPE ${String(session.perceivedExertion)}`
    } · ${humanise(session.status)}`,
    tone: SESSION_TONES[session.status] ?? 'mute',
  };
}

function LogsContent({ logs }: { readonly logs: AdminUserLogs }) {
  /**
   * The window is stated rather than implied. It is the same one the header's
   * adherence is measured over, resolved in the user's own timezone, so the two
   * numbers can never quietly disagree about which days they counted.
   */
  const window = `${logs.from} → ${logs.to} · ${String(logs.windowDays)} days`;
  const days = groupByDay(logs);

  return (
    <div className="flex flex-col gap-3">
      {/*
        Not in the imported design's Logs tab, and kept: weight is the only
        series on this screen that is a trend rather than a list, and it is the
        first thing anybody asks about a person on a cut.
      */}
      <Panel
        title="Weight"
        action={<span className="font-mono text-xs text-muted-foreground">{window}</span>}
      >
        <WeightChart points={logs.weights} />
      </Panel>

      <Panel
        title="Meal and workout log"
        description={`Last ${String(logs.windowDays)} days, newest first. A day with no row is a day nothing was logged.`}
        bleed
      >
        {days.length === 0 ? (
          <div className="border-t border-border">
            <Empty title="Nothing logged" body="No meal or workout was recorded in this window." />
          </div>
        ) : (
          <ul>
            {days.map((day) => (
              <li
                key={day.localDate}
                className="grid grid-cols-[92px_minmax(0,1fr)] gap-3.5 border-t border-border px-[18px] py-3 sm:grid-cols-[108px_minmax(0,1fr)]"
              >
                <span className="flex flex-col gap-0.5">
                  <span className="text-[13px] font-medium text-foreground">{formatDate(day.localDate)}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {day.kcal === 0 ? 'no meals' : `${String(day.kcal)} kcal`}
                  </span>
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {day.entries.map((entry) => (
                    <span
                      key={entry.key}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-xl border border-border bg-secondary px-2 py-1 text-[12.5px] text-foreground"
                    >
                      <span
                        aria-hidden="true"
                        className={cn('inline-block size-1.5 shrink-0 rounded-full', TONE_DOTS[entry.tone])}
                      />
                      {entry.label}
                      <span className="truncate text-muted-foreground">{entry.source}</span>
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
